<template>
  <v-autocomplete
    v-bind="$attrs"
    ref="field"
    v-model="localValue"
    :clearable="clearable && !$attrs.readonly && $attrs.readonly !== ''"
    :items="itemList"
    :loading="loading"
    no-filter
    :search-input.sync="match"
    @change="(v) => $emit('change', v)"
    @focus="(e) => e.target.click()"
  >
    <template #selection="data">
      <v-chip
        :selected="data.selected"
        close
        color="transparent"
        @input="removeItem(data.item)"
      >
        <span class="pr-2">
          {{ data.item.date }}
          -
          {{ data.item.time }}
        </span>
        <v-chip color="transparent">
          <v-avatar v-if="data.item.homeTeamLogo" size="32">
            <img :src="data.item.homeTeamLogo" />
          </v-avatar>
          <v-list-tile-content>
            <v-list-tile-title>
              <span>{{ data.item.homeTeam }}</span>
            </v-list-tile-title>

            <v-list-tile-sub-title
              class="caption"
              style="color: rgba(0, 0, 0, 0.54)"
            >
              {{ data.item.homeTeamCategory }}
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </v-chip>
        <span class="px-2">vs</span>
        <v-chip color="transparent">
          <v-avatar v-if="data.item.visitorTeamLogo" size="32">
            <img :src="data.item.visitorTeamLogo" />
          </v-avatar>
          <v-list-tile-content>
            <v-list-tile-title>
              <span>{{ data.item.visitorTeam }}</span>
            </v-list-tile-title>

            <v-list-tile-sub-title
              class="caption"
              style="color: rgba(0, 0, 0, 0.54)"
            >
              {{ data.item.visitorTeamCategory }}
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </v-chip>
      </v-chip>
    </template>
    <template #item="data">
      <v-chip color="transparent">
        <v-chip color="transparent" class="pr-2">
          {{ data.item.date }}
          -
          {{ data.item.time }}
        </v-chip>
        <v-chip color="transparent">
          <v-avatar size="32">
            <img :src="data.item.homeTeamLogo" />
          </v-avatar>
          <v-list-tile-content>
            <v-list-tile-title>
              <span>{{ data.item.homeTeam }}</span>
            </v-list-tile-title>

            <v-list-tile-sub-title
              class="caption"
              style="color: rgba(0, 0, 0, 0.54)"
            >
              {{ data.item.homeTeamCategory }}
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </v-chip>
        <span class="px-2">vs</span>
        <v-chip color="transparent">
          <v-avatar size="32">
            <img :src="data.item.visitorTeamLogo" />
          </v-avatar>
          <v-list-tile-content>
            <v-list-tile-title>
              <span>{{ data.item.visitorTeam }}</span>
            </v-list-tile-title>

            <v-list-tile-sub-title
              class="caption"
              style="color: rgba(0, 0, 0, 0.54)"
            >
              {{ data.item.visitorTeamCategory }}
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </v-chip>
      </v-chip>
    </template>
  </v-autocomplete>
</template>

<script>
import { debounce } from 'debounce'
export default {
  name: 'MatchField',
  props: {
    value: {
      type: [String, Array],
      required: false,
      default: '',
    },
    _id: {
      type: [String, Array],
      required: false,
      default: '',
    },
    tournamentId: {
      type: String,
      required: false,
      default: '',
    },
    categoryId: {
      type: String,
      required: false,
      default: '',
    },
    entityId: {
      type: String,
      required: false,
      default: '',
    },
    teamId: {
      type: String,
      required: false,
      default: '',
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    exclude: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      match: '',
      itemList: [],
      cacheList: [],
      loading: false,
      init: false,
    }
  },

  computed: {
    localValue: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
  },
  watch: {
    value() {
      this.fetch()
    },
    match() {
      this.fetch()
    },
    _id() {
      this.fetch()
    },
    categoryId() {
      this.fetch()
    },
    tournamentId() {
      this.fetch()
    },
    entityId() {
      if (!this.tournamentId) {
        this.fetch()
      }
    },
    teamId() {
      if (!this.tournamentId && !this.entityId) {
        this.fetch()
      }
    },
  },
  created() {
    this.fetch = debounce(function () {
      if (this.$refs.field && this.value !== this.$refs.field.lazyValue)
        this.$refs.field.lazyValue = this.value

      let params = { set: false }
      if (this.localValue && !Array.isArray(this.localValue)) {
        params._id = this.localValue
      } else if (
        Array.isArray(this.localValue) &&
        this.localValue.length &&
        !this.init
      ) {
        params._id = this.localValue
      } else {
        if (this._id) params._id = this._id
        else {
          if (this.categoryId) params.stadiumId = this.stadiumId
          else if (this.tournamentId) params.tournamentId = this.tournamentId

          if (this.entityId) params.entityId = this.entityId
          if (this.teamId) params.teamId = this.teamId

          if (this.match) params.match = this.match
        }
      }
      this.loading = true
      this.$store
        .dispatch('matches/fetchSelectList', params)
        .then((list) => {
          this.itemList = []
          list.forEach((item) => {
            if (
              Array.isArray(this.exclude) &&
              this.exclude.includes(item.value)
            )
              return

            if (item.homeTeamCategory && Array.isArray(item.homeTeamCategory))
              item.homeTeamCategory = item.homeTeamCategory.join(' - ')

            if (
              item.visitorTeamCategory &&
              Array.isArray(item.visitorTeamCategory)
            )
              item.visitorTeamCategory = item.visitorTeamCategory.join(' - ')

            this.itemList.push(item)
          })
          if (
            !Array.isArray(this.value) &&
            !this.itemList.some((item) => item.value === this.value)
          )
            this.value = ''
          this.setCache()
        })
        .finally(() => {
          this.loading = false
          this.init = true
        })
    }, 700)
    this.fetch()
  },
  methods: {
    fetch() {},
    setCache() {
      if (Array.isArray(this.localValue)) {
        this.localValue.forEach((v) => {
          const isCached = this.cacheList.some((item) => item.value === v)
          if (!isCached) {
            let item = this.itemList.find((item) => item.value === v)
            if (item) {
              this.cacheList.push(item)
            }
          }
        })
        this.cacheList = this.cacheList.filter((item) =>
          this.localValue.includes(item.value)
        )
        this.cacheList.forEach((cacheItem) => {
          const inList = this.itemList.some(
            (listItem) => listItem.value === cacheItem.value
          )
          if (!inList) this.itemList.push(cacheItem)
        })
      }
    },
    removeItem(item) {
      if (Array.isArray(this.localValue)) {
        const index = this.localValue.indexOf(item.value)
        if (index >= 0) this.localValue.splice(index, 1)
      } else this.localValue = ''
    },
  },
}
</script>
